import { queryOptions } from '@tanstack/react-query';
import moment from 'moment';
import { type PlacementDetails } from 'src/store/placementDetailsPageSlice/types';

import accommodationService from './accommodationService';

// **** Functions **** //

/**
 * Get placement detail data.
 */
const formatPlacementDetailsPayload = (payload: PlacementDetails) => {
  const newRevisions = payload.revisions
    // Normalize revision date to epoch milliseconds
    .map((revision) => ({
      ...revision,
      revisionDate: new Date(revision.revisionDate).getTime(),
    }))
    // Sort by revisionDate
    .sort((a, b) => new Date(a.revisionDate).getTime() - new Date(b.revisionDate).getTime());

  return {
    ...payload,
    startAtDate: moment(payload.startAtDate).format('L'),
    endAtDate: moment(payload.endAtDate).format('L'),
    transferredAtDate: moment(payload.transferredAtDate).format('L'),
    revisions: newRevisions,
  };
};

// **** Queries **** //

export const getAccommodationQueryOptions = {
  accommodationDetails: (accommodationId: number) =>
    queryOptions({
      queryKey: ['accommodation', accommodationId, 'accommodationDetail'] as const,
      queryFn: async () => (await accommodationService.getAccommodationDetail(accommodationId)).data,
      select: formatPlacementDetailsPayload,
      enabled: !!accommodationId,
    }),
  accommodationIdByCaseIdAndLogin: (type?: string, caseId?: string, login?: string) =>
    queryOptions({
      queryKey: ['accommodation', 'type', type, 'caseId', caseId, 'login', login] as const,
      queryFn: async () => (await accommodationService.getAccommodationIdByCaseIdAndLogin(type!, caseId!, login!)).data,
      enabled: !!type && !!caseId && !!login,
    }),
  emailTemplate: () =>
    queryOptions({
      queryKey: ['emailTemplate', 'saveAccommodation'] as const,
      queryFn: async () => (await accommodationService.getEmailTemplate()).data,
    }),
};

// **** Mutations **** //

export const getAccommodationMutationOptions = {
  delete: () => ({
    mutationFn: accommodationService.deleteAccommodation,
    meta: {
      successMessage: 'Deleted accommodation successfully',
    },
  }),
};
